import React, { useCallback, useEffect, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { CarrierConfig } from "../../model/SiteShipping";
import ShippingCarrierForm from "./ShippingCarrierForm";
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react";

const ShippingCarrierList: React.FC = () => {
  const [carriers, setCarriers] = useState<CarrierConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCarrier, setEditingCarrier] = useState<CarrierConfig | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);

  const fetchCarriers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(API_URLS.SHIPPING_CARRIERS);
      setCarriers(res.data?.data || res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar transportadoras.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCarriers();
  }, [fetchCarriers]);

  const handleToggleActive = async (carrier: CarrierConfig) => {
    try {
      await api.patch(`${API_URLS.SHIPPING_CARRIERS}/${carrier.id}`, {
        isActive: !carrier.isActive,
      });
      await fetchCarriers();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao atualizar estado.");
    }
  };

  const handleDelete = async (carrier: CarrierConfig) => {
    if (!confirm(`Eliminar transportadora "${carrier.displayName}"?`)) return;
    try {
      await api.delete(`${API_URLS.SHIPPING_CARRIERS}/${carrier.id}`);
      await fetchCarriers();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao eliminar.");
    }
  };

  const handleFormClose = (saved: boolean) => {
    setShowForm(false);
    setEditingCarrier(null);
    if (saved) fetchCarriers();
  };

  if (loading) return <div className="text-gray-500 py-4">A carregar...</div>;

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Transportadoras</h2>
        <button
          className="button is-primary is-small flex items-center gap-1"
          onClick={() => {
            setEditingCarrier(null);
            setShowForm(true);
          }}
        >
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      {carriers.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Nenhuma transportadora configurada.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 px-3">Nome</th>
                <th className="py-2 px-3">Slug</th>
                <th className="py-2 px-3">Unidade Peso</th>
                <th className="py-2 px-3">Modo Teste</th>
                <th className="py-2 px-3">Default</th>
                <th className="py-2 px-3">Estado</th>
                <th className="py-2 px-3 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((c) => (
                <tr key={c.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium">{c.displayName}</td>
                  <td className="py-2 px-3 text-gray-600">{c.carrierSlug}</td>
                  <td className="py-2 px-3">{c.weightUnit}</td>
                  <td className="py-2 px-3">
                    {c.testMode ? (
                      <span className="text-amber-600">Sim</span>
                    ) : (
                      "Nao"
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {c.isDefault ? (
                      <span className="text-blue-600 font-medium">Sim</span>
                    ) : (
                      "Nao"
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => handleToggleActive(c)}
                      className="flex items-center gap-1"
                      title={c.isActive ? "Desativar" : "Ativar"}
                    >
                      {c.isActive ? (
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      )}
                      <span
                        className={
                          c.isActive ? "text-green-600" : "text-gray-400"
                        }
                      >
                        {c.isActive ? "Ativo" : "Inativo"}
                      </span>
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        title="Editar"
                        onClick={() => {
                          setEditingCarrier(c);
                          setShowForm(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="text-red-500 hover:text-red-700"
                        title="Eliminar"
                        onClick={() => handleDelete(c)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ShippingCarrierForm
          carrier={editingCarrier}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default ShippingCarrierList;
